import { handleChange } from '@helpers/handleChange';
import { StoreState } from '@interfaces/stores';
import { invitationsService } from '@services/domain-factories/invitations';
import { reportsService } from '@services/domain-factories/reports';
import { responsesService } from '@services/domain-factories/responses';
import { createStoreWithSelector } from '@utils/zustand/createStoreWithSelector';
import { createUseStore } from '@utils/zustand/createUseStore';
import { initOnce } from '@utils/zustand/initOnce';
import { SurveyResponse } from '@vincerosoft/assessfluence-types';

type Actions = {
  handleChange: <T extends boolean>(
    change: T extends true ? string : SurveyResponse,
    isDelete?: T extends true ? T : never
  ) => void;
  getReports: (formIds: string[]) => Promise<void>;
  getAssociatedData: (responses: SurveyResponse[]) => Promise<void>;
  initResponsesStore: () => Promise<void>;
  getInvitations: (invitationIds: string[]) => Promise<void>;
};

const initialData: StoreState['responses'] = {
  isInitialized: false,
  responses: null,
  reports: null,
  inFlightInit: null,
  loading: false,
  referenceData: null,
  invitations: null
};

export const responsesStore = createStoreWithSelector<StoreState['responses'] & Actions>()(
  (set, get) => ({
    ...initialData,
    initResponsesStore: () =>
      initOnce(
        'isInitialized',
        set,
        get,
        () => responsesService.loadResponsesStore((responses) => set({ responses })),
        (responses) => {
          set({ responses, isInitialized: true });
        }
      ),

    handleChange: (change, isDelete) => {
      set((state) => ({
        responses: handleChange<SurveyResponse>(state.responses, change, isDelete)!
      }));
    },

    getReports: async (formIds) => {
      if (!formIds || formIds.length === 0) {
        set({ reports: [] });
        return;
      }
      await reportsService.getReportsByFormGroupIds(formIds, 'response', (reports) => {
        set({ reports });
      });
    },

    getInvitations: async (invitationIds) => {
      const storedData = get().invitations;

      if (storedData) {
        return;
      }

      const invitationBatches = await invitationsService.getInvitationsByIds(invitationIds);

      set({ invitations: invitationBatches.flat() });
    },

    getAssociatedData: async (responses) => {
      const storedData = get().referenceData;
      if (storedData) {
        return;
      }

      const referenceData = await responsesService.getResponsesAssociatedData(responses);
      set({ referenceData });
    }
  })
);

export const useResponsesStore = createUseStore(responsesStore);
export const responsesStoreActions = (): Actions => responsesStore.getState();
